import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import {
  People as PeopleIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';

interface StatsData {
  totalCaregivers: number;
  totalAppointments: number;
  averageRating: number;
  verifiedCaregivers: number;
}

interface StatsSectionProps {
  stats: StatsData | null;
  loading: boolean;
}

export const StatsSection: React.FC<StatsSectionProps> = ({ stats, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const statItems = [
    {
      icon: <PeopleIcon sx={{ fontSize: '2.5rem' }} />,
      value: stats?.totalCaregivers || 0,
      label: 'Registered Caregivers',
      suffix: '+',
      color: theme.palette.primary.main,
    },
    {
      icon: <ScheduleIcon sx={{ fontSize: '2.5rem' }} />,
      value: stats?.totalAppointments || 0,
      label: 'Successful Appointments',
      suffix: '+',
      color: theme.palette.secondary.main,
    },
    {
      icon: <StarIcon sx={{ fontSize: '2.5rem' }} />,
      value: stats?.averageRating || 0,
      label: 'Average Rating',
      suffix: '/5',
      color: theme.palette.warning.main,
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: '2.5rem' }} />,
      value: stats?.verifiedCaregivers || 0,
      label: 'Background Checked',
      suffix: '%',
      color: theme.palette.success.main,
    },
  ];

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 'bold',
              mb: 2,
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            Trusted by Thousands
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              opacity: 0.9,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Join the growing community of patients and caregivers who trust Careva 
            for quality, professional healthcare services.
          </Typography>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={4}>
          {statItems.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 2,
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </Box>

                {/* Value */}
                {loading ? (
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={60}
                    sx={{ mx: 'auto', mb: 1 }}
                  />
                ) : (
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{
                      fontWeight: 'bold',
                      mb: 1,
                      fontSize: { xs: '2rem', md: '2.5rem' },
                      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    {formatNumber(stat.value)}
                    <Box
                      component="span"
                      sx={{
                        fontSize: { xs: '1.2rem', md: '1.5rem' },
                        opacity: 0.8,
                      }}
                    >
                      {stat.suffix}
                    </Box>
                  </Typography>
                )}

                {/* Label */}
                {loading ? (
                  <Skeleton
                    variant="text"
                    width="90%"
                    height={24}
                    sx={{ mx: 'auto' }}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      opacity: 0.9,
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {stat.label}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Bottom Message */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontStyle: 'italic',
            }}
          >
            "Quality care, verified professionals, peace of mind"
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
